import type { Appearance, SetupIntent, Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { getEnv } from '@ctct/ctct-helpers';
import { observabilityAppService } from '@ctct/myaccount-utils-static';

import { STRIPE_ELEMENT_ID } from './constants';
import { StripeElementsFacade } from './StripeElementsFacade';
import {
  CommonPaymentMethods,
  type CreatePartnerSetupElementRequest,
  type CreatePartnerSetupElementResponse,
  type StripeSetupIntentResponse,
} from './types';
import { BaseApiClient } from '../BaseApiClient';
import { envs } from '../Environments';
import { myAccountApi } from '../MyAccountApi';

const PAYPAL_METHOD_ID_BY_ENV = {
  L1: 'cpmt_1RU6KkDAH2RRqhpoOMLtW12p',
  S1: 'cpmt_1S67zBD5lCLVMXqkZfXxob5q',
  Prod: 'cpmt_1RYrTFDKHHtgTaAY7Xov0xiX',
};

type CreateElementsCredentialsRequest = {
  quoteId?: string;
  countryCode: string | null;
  currencyCode: string | null;
  paymentMethodId?: string;
  returnUrl?: string;
  paymentMethodOptions?: { usBankAccount?: { verificationMethod: string } };
};

class StripeApi {
  private baseApiClient: BaseApiClient;

  private stripeElementsFacade: StripeElementsFacade | null = null;

  private normalizeCustomerPaymentMethodOptions(paymentMethodOptions?: {
    usBankAccount?: { verificationMethod: string };
  }) {
    const verificationMethod = paymentMethodOptions?.usBankAccount?.verificationMethod;

    if (!verificationMethod) {
      return paymentMethodOptions;
    }

    return {
      ...paymentMethodOptions,
      usBankAccount: {
        ...paymentMethodOptions.usBankAccount,
        verificationMethod: verificationMethod.toLowerCase(),
      },
    };
  }

  private buildPartnerSetupElementPayload({
    setAsDefault,
    countryCode,
    currencyCode,
    returnUrl,
    paymentMethodOptions,
  }: Omit<CreatePartnerSetupElementRequest, 'partnerPuid'>) {
    return {
      setAsDefault,
      countryCode,
      currencyCode,
      returnUrl,
      paymentMethodOptions,
    };
  }

  constructor() {
    const baseUrl = `https://app${getEnv()}constantcontact.com/v1/stripe`;

    this.baseApiClient = new BaseApiClient(baseUrl);
  }

  public async getPublishableKey(): Promise<string> {
    const response = await myAccountApi.getPublishableKey();

    return response.data.key;
  }

  private getPaypalMethodId(): string {
    if (envs.isL1()) {
      return PAYPAL_METHOD_ID_BY_ENV.L1;
    }

    if (envs.isS1()) {
      return PAYPAL_METHOD_ID_BY_ENV.S1;
    }

    if (envs.isProd()) {
      return PAYPAL_METHOD_ID_BY_ENV.Prod;
    }

    observabilityAppService.notify('#stripe Paypal method id not found for environment', { environment: getEnv() });
    throw new Error(`Paypal method id not found for environment "${getEnv()}"`);
  }

  public getPaymentMethodIds(): { card: string; paypal: string; pad: string; usBankAccount: string } {
    return {
      card: CommonPaymentMethods.Card,
      paypal: this.getPaypalMethodId(),
      pad: CommonPaymentMethods.PreAuthorizedDebit,
      usBankAccount: CommonPaymentMethods.UsBankAccount,
    };
  }

  public async createElementsCredentials({
    quoteId,
    countryCode,
    currencyCode,
    paymentMethodId,
    returnUrl,
    paymentMethodOptions,
  }: CreateElementsCredentialsRequest): Promise<StripeSetupIntentResponse> {
    const payload = {
      ...(quoteId ? { quoteId } : {}),
      countryCode,
      currencyCode,
      paymentMethodId,
      returnUrl,
      paymentMethodOptions: this.normalizeCustomerPaymentMethodOptions(paymentMethodOptions),
    };

    return (await this.baseApiClient.post<StripeSetupIntentResponse, typeof payload>('/setup-element', payload)).data;
  }

  public async updateSetupIntentParams({
    quoteId,
    countryCode,
    currencyCode,
    paymentMethodId,
    returnUrl,
    paymentMethodOptions,
  }: {
    quoteId: string | null;
    countryCode?: string;
    currencyCode?: string;
    paymentMethodId?: string;
    returnUrl?: string;
    paymentMethodOptions?: { usBankAccount?: { verificationMethod: string } };
  }): Promise<StripeSetupIntentResponse & { setupIntentId: string }> {
    return (
      await this.baseApiClient.post<StripeSetupIntentResponse & { setupIntentId: string }, unknown>('/setup-element', {
        quoteId,
        countryCode,
        currencyCode,
        paymentMethodId,
        returnUrl,
        paymentMethodOptions: this.normalizeCustomerPaymentMethodOptions(paymentMethodOptions),
      })
    ).data;
  }

  public async createPartnerSetupElement({
    partnerPuid,
    setAsDefault,
    countryCode,
    currencyCode,
    returnUrl,
    paymentMethodOptions,
  }: CreatePartnerSetupElementRequest): Promise<CreatePartnerSetupElementResponse> {
    const payload = this.buildPartnerSetupElementPayload({
      setAsDefault,
      countryCode,
      currencyCode,
      returnUrl,
      paymentMethodOptions,
    });

    return (
      await this.baseApiClient.post<CreatePartnerSetupElementResponse, typeof payload>(
        `/partner/${partnerPuid}/setup-element`,
        payload
      )
    ).data;
  }

  /**
   * Note: if we need to make this public, we need to make it a singleton
   */
  private async getStripe(): Promise<Stripe> {
    const publishableKey = await this.getPublishableKey();
    const stripe = await loadStripe(publishableKey);

    if (!stripe) {
      throw new Error('Failed to initialize Stripe');
    }

    return stripe;
  }

  private getStripeElement() {
    const stripeElement = document.getElementById(STRIPE_ELEMENT_ID);

    if (!stripeElement) {
      throw new Error('Stripe element not found');
    }

    return stripeElement;
  }

  public getStripeElementId(): string {
    return STRIPE_ELEMENT_ID;
  }

  public async getStripeElementsFacade(): Promise<StripeElementsFacade> {
    if (this.stripeElementsFacade) {
      return this.stripeElementsFacade;
    }

    const stripe = await this.getStripe();

    const facade = new StripeElementsFacade(stripe, () => this.getStripeElement());

    this.stripeElementsFacade = facade;

    return facade;
  }

  public getReturnUrl(): string {
    const url = new URL(window.location.href);

    // We intentionally strip Stripe-specific redirect params from the current URL so that each new redirect starts from a "clean" base URL.
    // Otherwise, if the user goes through multiple redirect Stripe will append new `setup_intent`, `setup_intent_client_secret` and `redirect_status` values onto a URL that already contains older, stale values.
    url.searchParams.delete('setup_intent');
    url.searchParams.delete('setup_intent_client_secret');
    url.searchParams.delete('redirect_status');

    return url.toString();
  }

  public getDefaultStripeAppearance(): Appearance {
    return {
      theme: 'stripe',
      variables: {
        colorPrimary: '#0570de',
        colorBackground: '#ffffff',
        colorText: '#30313d',
        colorDanger: '#df1b41',
        fontFamily: 'Ideal Sans, system-ui, sans-serif',
      },
    };
  }

  public readPostRedirectUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);

    const setupIntent = urlParams.get('setup_intent');
    const setupIntentClientSecret = urlParams.get('setup_intent_client_secret');
    const redirectStatus = urlParams.get('redirect_status');

    if (!setupIntent || !setupIntentClientSecret || !redirectStatus) {
      return null;
    }

    return {
      setupIntent,
      setupIntentClientSecret,
      redirectStatus,
    };
  }

  public async retrieveSetupIntent(clientSecret: string) {
    const stripe = await this.getStripe();

    const { setupIntent } = await stripe.retrieveSetupIntent(clientSecret);

    return setupIntent;
  }

  public async handleNextAction(setupIntent: SetupIntent) {
    const nextAction = setupIntent.next_action;

    if (!nextAction) {
      return;
    }

    switch (nextAction.type) {
      case 'redirect_to_url': {
        const url = nextAction.redirect_to_url?.url;

        if (!url) {
          throw new Error('redirect_to_url.url is not present in the setup intent');
        }

        window.location.href = url;
        break;
      }

      case 'use_stripe_sdk': {
        const stripe = await this.getStripe();

        if (!setupIntent.client_secret) {
          throw new Error('client_secret is not present in the setup intent');
        }

        await stripe.handleNextAction({ clientSecret: setupIntent.client_secret });

        break;
      }

      case 'verify_with_microdeposits': {
        throw new Error('Microdeposit verification not yet supported');
      }

      default: {
        throw new Error(`Unsupported next action type: ${nextAction.type}`);
      }
    }
  }
}

/**
 * Represents an API for working with the Stripe Elements client-side SDK.
 *
 * For fetching information about the customer's Stripe account, use `stripeAccountApi` instead.
 */
export const stripeApi = new StripeApi();
